import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../api/company';
import {
  workspacesApi,
  clearWorkspaceId,
  getWorkspaceId,
  setWorkspaceId,
  type WorkspaceInfo,
} from '../api/workspaces';
import CopilotPanel, { getCopilotStorage, toggleCopilotStorage, type AiAction } from './CopilotPanel';
import CommandPalette from './CommandPalette';
import AIStatusBar from './AIStatusBar';
import LanguageSwitcher from './LanguageSwitcher';
import ProfileMenu from './ProfileMenu';
import { useScreenData, executeAIAction, executeAIFill, scanDOMActions, scanDOMFields, scanDOMTableData, scanActiveRecord, type ScreenData } from './ScreenContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useModules } from '../hooks/useModules';
import { useAppBackFloor } from '../hooks/useAppBackFloor';
import { isAdmin } from '../api/client';
import { exceptionsApi } from '../api/aiAuthority';

// Navigation items organized per v2 spec with 12 top-level modules.
// Items with moduleId are only shown when that module is active.
// 'type: section' creates a collapsible group header.
// Label keys are i18n translation keys resolved via useTranslation.
interface NavItem {
  to?: string;
  labelKey: string;
  icon: string;
  moduleId?: string;
  adminOnly?: boolean;
  type?: string;
  feature?: string;
}

const NAV_ITEMS: NavItem[] = [
  // 1b. Darbai — per-company work queue (feature-flagged, egress-free).
  { to: '/analytics', labelKey: 'nav.darbai', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', feature: 'analytics', moduleId: 'Work' },



  // 1d. Uždarymas — the guided close (wizard over the existing periods spine).
  { to: '/uzdarymas', labelKey: 'nav.closeWizard', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', feature: 'analytics' },

  // Sąskaitos — one module, three tabs (Pardavimai | Pirkimai | Skenavimas). The
  // buy/sell split lives in the DATA model (InvoiceMade/InvoiceReceived) and in the
  // page tabs; one accountant crosses directions constantly, so the nav unifies.
  { to: '/invoices/made', labelKey: 'nav.invoices', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', moduleId: 'Invoices' },

  // 4. Banking & Cash
  { to: '/banking', labelKey: 'nav.bankingCash', icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3', moduleId: 'Banking' },

  // 4b. Payroll (module-gated) — the pages existed at /payroll since P1a but no
  // nav entry ever linked to them; enabling the module surfaced nothing.
  { to: '/payroll', labelKey: 'nav.payroll', icon: 'M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm9-2h.01', moduleId: 'Payroll' },

  // 4c. Advance reports (module-gated) — same gap as payroll.
  { to: '/advance-reports', labelKey: 'nav.advanceReports', icon: 'M9 1h6M9 4h6M8 7h8a2 2 0 012 2v10a2 2 0 01-2 2H8a2 2 0 01-2-2V9a2 2 0 012-2z', moduleId: 'AdvanceReports' },

  // 5. Journal
  { to: '/journal', labelKey: 'nav.journal', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },

  // 6. Assets (module-gated)
  { to: '/fixed-assets', labelKey: 'nav.assets', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', moduleId: 'Assets' },

  // 7. Reports
  { to: '/reports/vat', labelKey: 'nav.reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },

  // 7b. Mano užklausos — the user's own reports to the operator (status
  // tracking). Promoted from the profile dropdown back into the sidebar per
  // finding 44 (2026-09-12, owner feedback): users file requests mid-work and
  // want it beside Sąskaitos/Žurnalas/Ataskaitos. Still ONE entry per
  // capability — the profile-menu link was removed with the same change.
  { to: '/mano-uzklausos', labelKey: 'nav.myFindings', icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },

  // 8. Tax & Compliance
  { to: '/reports/saft', labelKey: 'nav.taxCompliance', icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z' },

  // 9. Close
  { to: '/periods/close', labelKey: 'nav.close', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },

  // 10. Setup (group header with sub-items)
  { labelKey: 'nav.setup', icon: 'M4 6h16M4 10h16M4 14h16M4 18h16', type: 'section' },
  // 10a'. Automatizavimas — routine AUTHORING is setup, not daily work; the daily
  // routine drafts arrive as Darbai cards anyway.
  { to: '/automatizavimas', labelKey: 'nav.automation', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', feature: 'analytics', moduleId: 'Work' },

  // 10a. Users — promote bridge-provisioned Viewers to Accountant (admin only).
  { to: '/settings/users', labelKey: 'nav.users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', adminOnly: true },
  // 10a". Moduliai — per-workspace module toggles (ADR-007; admin only).
  { to: '/settings/modules', labelKey: 'nav.modules', icon: 'M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z', adminOnly: true },
  { to: '/accounts', labelKey: 'nav.accounts', icon: 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
  { to: '/persons', labelKey: 'nav.contacts', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { to: '/shares', labelKey: 'nav.shares', icon: 'M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z' },
  { to: '/catalog', labelKey: 'nav.catalog', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', moduleId: 'Inventory' },
  { to: '/inventory', labelKey: 'nav.inventoryList', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', moduleId: 'Inventory' },

  // 11. AI & Audit — admin only (operator diagnostics; AI data egress is per-workspace gated)
  { to: '/ai-audit', labelKey: 'nav.aiAudit', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', adminOnly: true, moduleId: 'Assistant' },

  // 11b. Arena — admin only (agent diagnostics surface)
  { to: '/arena', labelKey: 'nav.arena', icon: 'M13 10V3L4 14h7v7l9-11h-7z', adminOnly: true, moduleId: 'Assistant' },

  // 11c. Radiniai — admin only (copilot findings triage board + dev worker plane)
  { to: '/admin/findings', labelKey: 'nav.findings', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', adminOnly: true },

  // 12. Settings
  { to: '/settings', labelKey: 'nav.settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
];

// Map route paths to human-readable context labels (Lithuanian, with diacritics —
// this string is visible in the top bar every day).
const PATH_CONTEXT: Record<string, string> = {
  '/': 'Pradžia',
  '/darbai': 'Visos įmonės',
  '/invoices/scan': 'Sąskaitos — skenavimas',
  '/invoices/template': 'Sąskaitos — šablonas',
  '/documents': 'Sąskaitos — skenavimas',
  '/analytics': 'Darbai — įmonė',
  '/invoices': 'Sąskaitos faktūros',
  '/invoices/made': 'Sąskaitos — pardavimai',
  '/invoices/received': 'Sąskaitos — pirkimai',
  '/invoices/new': 'Nauja sąskaita faktūra',
  '/invoices/credit-note/new': 'Kreditinė sąskaita faktūra',
  '/banking': 'Bankinės operacijos',
  '/banking/accounts': 'Banko sąskaitos',
  '/banking/import': 'Banko išklotinės importas',
  '/cash-operations': 'Kasos operacijos',
  '/offsets': 'Užskaitos',
  '/advance-reports': 'Avansinės ataskaitos',
  '/journal': 'Žurnalas',
  '/journal/new': 'Naujas žurnalo įrašas',
  '/accounts': 'Sąskaitų planas',
  '/accounts/trial-balance': 'Bandomasis balansas',
  '/persons': 'Kontaktai',
  '/catalog': 'Prekių ir paslaugų katalogas',
  '/fixed-assets': 'Ilgalaikis turtas',
  '/inventory': 'Atsargos',
  '/inventory/operations': 'Prekių operacijos',
  '/payroll': 'Darbo užmokestis',
  '/payroll/contracts': 'Darbuotojų sutartys',
  '/payroll/work-time': 'Darbo laiko apskaita',
  '/payroll/config': 'DUŽ tarifai ir sąskaitos',
  '/payroll/sheets': 'DUŽ žiniaraščiai',
  '/payroll/imprests': 'Avansai',
  '/payroll/non-insurable': 'Nedraudiminiai laikotarpiai',
  '/payroll/other-payouts': 'Kitos išmokos',
  '/payroll/vmi-declarations': 'VMI deklaracijos',
  '/reports/vat': 'PVM ataskaita',
  '/reports/vat-declaration': 'PVM deklaracija',
  '/reports/financial-statements': 'Finansinės ataskaitos',
  '/reports/debt-aging': 'Skolų senumo ataskaita',
  '/mano-uzklausos': 'Mano užklausos',
  '/reports/saft': 'Mokesčiai ir atitiktis',
  '/periods/close': 'Periodo uždarymas',
  '/audit': 'Audito žurnalas',
  '/ai-audit': 'AI ir auditas',
  '/arena': 'Agentų arena',
  '/admin/findings': 'Radiniai',
  '/settings': 'Nustatymai',
  '/settings/roles': 'Vaidmenys',
  '/settings/users': 'Naudotojai',
  '/uzdarymas': 'Uždarymo vedlys',
  '/automatizavimas': 'Automatizavimas',
  '/settings/backup': 'Atsarginė kopija',
  '/settings/email': 'El. pašto konfigūracija',
  '/workspaces': 'Darbo sritys',
};

function getPageContext(pathname: string): string {
  // Exact match first
  if (PATH_CONTEXT[pathname]) return PATH_CONTEXT[pathname];
  // Check for dynamic routes
  if (pathname.startsWith('/invoices/') && pathname.includes('/edit')) return 'Redaguojama sąskaita faktūra';
  if (pathname.startsWith('/journal/') && pathname.endsWith('/edit')) return 'Redaguojamas žurnalo įrašas';
  if (pathname.startsWith('/invoices/')) return 'Sąskaita faktūra';
  if (pathname.startsWith('/journal/')) return 'Žurnalo įrašas';
  // Fallback
  return pathname;
}

export default function Layout({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workspaceDropdown, setWorkspaceDropdown] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(getCopilotStorage);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const screenData = useScreenData();
  const { enableAiBuddy, enableAnalytics } = useFeatureFlags();
  const { isModuleActive, reload: reloadModules } = useModules();
  const admin = isAdmin();

  // Filter nav items: module items only when the ACTIVE WORKSPACE has the module
  // enabled (ADR-007 server-backed set), admin-only items only for Admins,
  // feature-flagged items only when their flag is on. The daily loop is
  // FULLY visible (go-live decision: the accountant must see suderinti bank,
  // pirkimų juodraščiai, žurnalas without URL knowledge). Kept hidden: the manual
  // /periods/close, /reports/saft, /darbai (portfolio — per-company isolation),
  // /documents (the scan lives inside Sąskaitos) and /uzdarymas — the close is
  // MONTHLY work, not a daily destination: its entries are the Darbai close card,
  // the Ctrl+K palette and the direct URL, so the nav slot over-promoted it.
  const HIDDEN_NAV = new Set(['/periods/close', '/reports/saft', '/darbai', '/documents', '/uzdarymas']);
  const visibleNavItems = NAV_ITEMS.filter(item => {
    if (item.adminOnly) return admin;
    if (item.moduleId && !isModuleActive(item.moduleId)) return false;
    if (item.to !== undefined && HIDDEN_NAV.has(item.to)) return false;
    if (item.feature === 'analytics' && !enableAnalytics) return false;

    return true;
  });

  const { data: workspaces } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
  });

  const activeWorkspace = workspaces?.find((w: WorkspaceInfo) => w.isActive);
  // Sync: ensure workspace ID is in localStorage before child components render.
  // Self-heal a STALE id (its workspace deleted — the Stašaičio trap 2026-09-11:
  // every request silently targeted a dead context while break-glass let them
  // through): if the stored id is not among the user's workspaces, fall back to
  // the active one rather than haunting the deleted one forever.
  const storedWorkspaceId = getWorkspaceId();
  const storedStillExists = !storedWorkspaceId
    || (workspaces ?? []).some((w: WorkspaceInfo) => w.id === storedWorkspaceId);
  if (workspaces !== undefined && storedWorkspaceId && !storedStillExists) {
    if (activeWorkspace) setWorkspaceId(activeWorkspace.id);
    else clearWorkspaceId();
  }
  if (activeWorkspace && !getWorkspaceId()) {
    setWorkspaceId(activeWorkspace.id);
  }
  const hasWorkspace = !!getWorkspaceId();

  const { data: company } = useQuery({
    queryKey: ['company'],
    queryFn: companyApi.get,
    enabled: hasWorkspace,
  });

  // Exceptions count for badge on AI & Audit nav item
  const { data: exceptionsCount } = useQuery<number>({
    queryKey: ['exceptions-count'],
    queryFn: () => exceptionsApi.getCount(),
    enabled: hasWorkspace,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const handleSwitchWorkspace = async (workspace: WorkspaceInfo) => {
    try {
      await workspacesApi.activate(workspace.id);
      setWorkspaceId(workspace.id);
      setWorkspaceDropdown(false);
      // Invalidate all queries to refetch with new workspace
      queryClient.invalidateQueries();
      // Modules are per-workspace (ADR-007) — re-read the effective set.
      await reloadModules();
    } catch (err) {
      // Silent failures here once cost an hour of debugging (403 from an
      // admin-gated activate endpoint) — always surface the reason.
      alert(err instanceof Error ? `Nepavyko perjungti darbo srities: ${err.message}` : 'Nepavyko perjungti darbo srities');
    }
  };

  // Initialize workspace ID on mount
  useEffect(() => {
    if (activeWorkspace && !getWorkspaceId()) {
      setWorkspaceId(activeWorkspace.id);
    }
  }, [activeWorkspace]);

  // Finding 35: the browser Back button never leaves the app (it used to pop
  // past the SPA entry and kill the session mid-work). The floor absorbs the
  // exit; in-app Back between routes still works.
  useAppBackFloor();

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K or Cmd+K: open command palette
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
      // Escape: close command palette (copilot handles its own escape)
      if (e.key === 'Escape' && commandPaletteOpen) {
        setCommandPaletteOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandPaletteOpen]);

  // Listen for external copilot-open events (e.g., from arena sendCopilotMessage)
  useEffect(() => {
    const handler = () => {
      setCopilotOpen(true);
      toggleCopilotStorage(true);
    };
    window.addEventListener('copilot-open', handler);
    return () => window.removeEventListener('copilot-open', handler);
  }, []);

  const toggleCopilot = useCallback(() => {
    setCopilotOpen(prev => {
      const next = !prev;
      toggleCopilotStorage(next);
      return next;
    });
  }, []);

  const handleCopilotClose = useCallback(() => {
    setCopilotOpen(false);
    toggleCopilotStorage(false);
  }, []);

  const handleStatusBarClick = useCallback(() => {
    navigate('/ai-audit');
  }, [navigate]);

  const handleAiAction = useCallback((action: AiAction) => {
    // Direct workspace activation — bypasses dropdown entirely
    if (action.actionType === 'activate_workspace' && action.target) {
      const target = action.target.trim().toLowerCase();
      const ws = workspaces?.find((w: WorkspaceInfo) =>
        w.name.toLowerCase() === target || String(w.id) === action.target
      );
      if (ws) {
        handleSwitchWorkspace(ws);
      } else {
        console.warn('AI activate_workspace: workspace not found:', action.target, 'Available:', workspaces?.map((w: WorkspaceInfo) => w.name));
      }
      return;
    }
    if (action.actionType === 'ui_click' && action.target) {
      executeAIAction(action.target);
      return;
    }
    if (action.actionType === 'ui_fill' && action.target) {
      executeAIFill(action.target, action.value ?? '');
      return;
    }
    // navigate with optional prefill
    const path = action.path;
    if (!path || !path.startsWith('/') || path.includes('//') || path.includes(':')) {
      console.warn('AI action rejected: invalid path', path);
      return;
    }
    if (action.formData) {
      navigate(path, { state: { aiPrefill: { ...action.formData, explanation: action.reason } } });
    } else {
      navigate(path);
    }
  }, [navigate]);

  const pageContextLabel = getPageContext(location.pathname);
  const pageContext = [
    pageContextLabel,
    company ? `${t('layout.company')}: ${company.name}` : '',
    activeWorkspace?.name ? `${t('layout.area')}: ${activeWorkspace.name}` : '',
  ].filter(Boolean).join(' | ');

  // Build structured screen data for the AI copilot (sent as screenData with
  // every message; the bridge injects it into the system prompt).
  // Scans DOM for all interactive elements (explicit data-ai-* attrs + semantic HTML), merges with page-reported data.
  // Also scans visible table data and active/selected records for AI context.
  // NEVER returns null -- always provides at least the page context so the AI knows where the user is.
  const buildScreenData = (): ScreenData => {
    const domActions = scanDOMActions();
    const domFields = scanDOMFields();
    const domTableData = scanDOMTableData();
    const domActiveRecord = scanActiveRecord();
    const hasAnyData = screenData.data || domActions.length > 0 || domFields.length > 0;
    const base = hasAnyData ? (screenData.data || { summary: '' }) : { summary: pageContextLabel };
    return {
      summary: base.summary || pageContextLabel,
      recordCount: base.recordCount,
      filters: base.filters,
      preview: base.preview,
      tableData: base.tableData && base.tableData.length > 0 ? base.tableData : (domTableData.length > 0 ? domTableData : undefined),
      activeRecord: base.activeRecord || domActiveRecord || undefined,
      formFields: [
        ...(base.formFields || []),
        ...domFields.filter(df => !(base.formFields || []).some(pf => pf.name === df.name)),
      ],
      updatedAt: base.updatedAt,
      actions: [
        ...(base.actions || []),
        ...domActions.filter(da => !(base.actions || []).some(pa => pa.id === da.id)),
      ],
      // Expose workspace list so AI can switch workspaces directly via activate_workspace action
      workspaces: workspaces?.map((ws: WorkspaceInfo) => ({
        id: ws.id,
        name: ws.name,
        isActive: ws.isActive,
        companyName: ws.companyName,
      })) ?? [],
      activeWorkspace: activeWorkspace ? { id: activeWorkspace.id, name: activeWorkspace.name } : null,
    };
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary-100 text-primary-800'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      {/* Mobile drawer sits above the CopilotPanel sheet (z-[70]); keep z-30 on
          lg so desktop modals (z-50) still stack above the relative sidebar. */}
      <aside className={`fixed inset-y-0 left-0 z-[80] lg:z-30 w-56 bg-white border-r border-gray-200 transform transition-transform lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo / Company — fixed h-14 to match the main header exactly.
              Logo at h-7 (not h-8): a touch smaller so the fixed 56px band
              reads as generous white space around it. */}
          <div className="flex items-center h-14 px-5 border-b border-gray-200">
            <h1 className="mb-0">
              <img src="/logo.png" alt="A5Suite" className="h-[22px]" />
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item, idx) => {
              // Section headers (no navigation)
              if (item.type === 'section') {
                return (
                  <div key={`section-${idx}`} className="pt-3 pb-1 px-3">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t(item.labelKey)}</span>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.to}
                  to={item.to!}
                  end={item.to === '/'}
                  className={linkClass}
                  onClick={() => setSidebarOpen(false)}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                  </svg>
                  <span className="flex-1">{t(item.labelKey)}</span>
                  {/* Admin-only marker */}
                  {item.adminOnly && (
                    <span className="flex-shrink-0 text-gray-400" title={t('nav.adminOnly')}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                      </svg>
                    </span>
                  )}
                  {/* Exceptions badge on AI & Audit */}
                  {item.to === '/ai-audit' && exceptionsCount && exceptionsCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {exceptionsCount > 99 ? '99+' : exceptionsCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-400">
            A5Suite {__APP_VERSION__}
          </div>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[75] bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="flex items-center gap-4 px-4 h-14 bg-white border-b border-gray-200">
          <button
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Workspace Selector — with the read-only badge docked next to it
              (finding 39: the mode applies to the SELECTED workspace, so the
              indicator lives at the selector, not in the far corner) */}
          <div className="relative flex items-center gap-2">
            <button
              data-ai-action="workspace-dropdown"
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
              onClick={() => setWorkspaceDropdown(!workspaceDropdown)}
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              <span className="max-w-[240px] truncate font-medium">
                {activeWorkspace?.companyName ?? activeWorkspace?.name ?? t('layout.noWorkspace')}
              </span>
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Read-only plane: a mysql-synced hosted book — a5suite views it,
                Apskaita5 owns the writes (two-plane rule). Docked at the
                workspace selector so it's clear the mode applies to THIS book;
                the tooltip says where changes happen. */}
            {activeWorkspace?.sourceKind === 'mysql' && (
              <span
                data-ai-element="read-only-badge"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                title={t('analytics.hostedReadOnly')}
              >
                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="hidden lg:inline">{t('layout.readOnly')}</span>
              </span>
            )}

            {workspaceDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setWorkspaceDropdown(false)} />
                <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase">{t('layout.workspaces')}</p>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {workspaces?.map((ws: WorkspaceInfo) => (
                      <button
                        key={ws.id}
                        data-ai-action={`workspace-${(ws.companyName ?? ws.name).toLowerCase().replace(/\s+/g, '_')}`}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                          ws.isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                        }`}
                        onClick={() => handleSwitchWorkspace(ws)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{ws.companyName ?? ws.name}</div>
                        </div>
                        {ws.isActive && (
                          <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                    {(!workspaces || workspaces.length === 0) && (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">{t('workspace.noWorkspaces')}</div>
                    )}
                  </div>
                  {/* Operator-only: workspace/company management (import client db,
                      create new company). */}
                  {admin && (
                    <div className="border-t border-gray-100 px-3 py-2 flex gap-2">
                      <button
                        className="flex-1 text-xs text-primary-600 hover:text-primary-800 font-medium py-1"
                        onClick={() => {
                          setWorkspaceDropdown(false);
                          navigate('/workspaces');
                        }}
                      >
                        {t('layout.manageWorkspaces')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex-1" />

          {/* Command palette trigger — centered in the header, wide */}
          <button
            onClick={() => setCommandPaletteOpen(true)}
            className="flex items-center justify-center gap-2 w-72 sm:w-[28rem] px-3 py-2 text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
            title={t('layout.searchCtrlK')}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="hidden sm:inline truncate">{t('common.search')}...</span>
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-white rounded border border-gray-200 ml-auto">
              Ctrl+K
            </kbd>
          </button>

          <div className="flex-1" />

          {/* The chat PANEL button — the ONE entry to the docked chat (the popping
              buddy stays removed). Labeled so it's findable; icon-only on small
              screens. Hidden unless AI is feature-flagged on. */}
          {enableAiBuddy && (
            <button
              onClick={toggleCopilot}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                copilotOpen
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              title={t('analytics.ask.link')}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <span className="hidden sm:inline">{t('analytics.ask.link')}</span>
            </button>
          )}

          {/* Page context indicator */}
          <span className="text-sm text-gray-500 hidden md:inline">{pageContextLabel}</span>

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Profile — the logged-in identity (email + role) with sign-out,
              mirroring the integravizija.lt avatar. */}
          <ProfileMenu onLogout={onLogout} />
        </header>

        {/* Content area with optional copilot */}
        <div className="flex-1 flex overflow-hidden">
          {/* Page content */}
          <main className="flex-1 overflow-y-auto p-4 lg:p-6">
            <Outlet />
          </main>

          {/* Copilot panel — only when AI is feature-flagged on. On the ask
              analytics surface the SAME buddy runs the TurnSpec composer and
              starts centered before docking here. */}
          {enableAiBuddy && (
            <CopilotPanel
              open={copilotOpen}
              onClose={handleCopilotClose}
              pageContext={`${location.pathname} - ${pageContext}`}
              screenData={buildScreenData()}
              onAction={handleAiAction}
              askMode={location.pathname.startsWith('/analytics/ask')}
            />
          )}
        </div>

        {/* AI Status Bar — only when AI is feature-flagged on; admin-only (its only
            affordance opens /ai-audit; the same data lives on the Dashboard tiles) */}
        {enableAiBuddy && admin && <AIStatusBar onClick={handleStatusBarClick} />}
      </div>

      {/* Command Palette (global overlay) */}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />

      {/* AIFloatingBuddy removed — ONE entry to the chat: the top-bar Klausti toggle. */}
    </div>
  );
}
